/**
 * Data Transformer Service
 * Handles Excel file parsing, validation, and transformation to MSDAT format
 */
import * as XLSX from 'xlsx';

/**
 * Required columns for MSDAT data format
 */
export const REQUIRED_COLUMNS = ['indicator', 'location', 'period', 'value', 'datasource'];

/**
 * Optional columns that enhance data if present
 */
export const OPTIONAL_COLUMNS = ['value_type', 'gender', 'age_group', 'category'];

const isBlankValue = (value) => value === '' || value === null || value === undefined
  || (typeof value === 'string' && value.trim() === '');

/**
 * Parse an Excel file and extract data from all sheets
 * @param {File} file - The Excel file to parse
 * @param {Object} options - Parser limits
 * @returns {Promise<{workbook: Object, sheets: Object, sheetNames: string[], totalRows: number}>}
 */
export function parseExcelFile(file, options = {}) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheets = {};
        let totalRows = 0;
        const maxRows = options.maxRows || null;

        workbook.SheetNames.forEach((sheetName) => {
          const worksheet = workbook.Sheets[sheetName];
          const range = worksheet['!ref'] ? XLSX.utils.decode_range(worksheet['!ref']) : null;
          const sheetRows = range ? Math.max(range.e.r - range.s.r, 0) : 0;

          totalRows += sheetRows;
          if (maxRows && totalRows > maxRows) {
            throw new Error(`This workbook has more than ${maxRows.toLocaleString()} rows. Please upload a smaller file.`);
          }

          sheets[sheetName] = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
        });

        resolve({
          workbook,
          sheets,
          sheetNames: workbook.SheetNames,
          totalRows,
        });
      } catch (error) {
        reject(new Error(`Failed to parse Excel file: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsArrayBuffer(file);
  });
}

/**
 * Extract column headers from parsed sheet data
 * @param {Array} sheetData - Array of row objects from a sheet
 * @returns {string[]} - Array of column names
 */
export function extractColumns(sheetData) {
  if (!sheetData || sheetData.length === 0) {
    return [];
  }
  return Object.keys(sheetData[0]);
}

/**
 * Auto-detect column mappings based on common naming patterns
 * @param {string[]} columns - Available column names
 * @returns {Object} - Suggested column mappings
 */
export function autoDetectColumnMapping(columns) {
  const mapping = {};
  const lowerColumns = columns.map((col) => ({ original: col, lower: col.toLowerCase() }));

  const patterns = {
    indicator: ['indicator', 'indicator_id', 'indicatorid', 'indicator_name', 'indicatorname', 'ind'],
    location: ['location', 'location_id', 'locationid', 'state', 'lga', 'region', 'area', 'geo', 'geography'],
    period: ['period', 'year', 'date', 'time', 'timeperiod', 'time_period', 'reporting_period'],
    value: ['value', 'data_value', 'datavalue', 'amount', 'count', 'number', 'val'],
    datasource: ['datasource', 'data_source', 'source', 'source_id', 'sourceid', 'survey'],
    value_type: ['value_type', 'valuetype', 'type', 'unit', 'measure_type'],
    gender: ['gender', 'sex'],
    age_group: ['age_group', 'agegroup', 'age', 'age_range'],
    category: ['category', 'subcategory', 'group', 'classification'],
  };

  Object.entries(patterns).forEach(([field, patternList]) => {
    patternList.some((pattern) => {
      const match = lowerColumns.find((col) => col.lower === pattern || col.lower.includes(pattern));
      if (match && !mapping[field]) {
        mapping[field] = match.original;
        return true;
      }
      return false;
    });
  });

  return mapping;
}

/**
 * Validate that the data schema contains all required columns
 * @param {Array} data - The parsed data array
 * @param {Object} columnMapping - The column mapping configuration
 * @returns {{isValid: boolean, errors: string[], warnings: string[]}}
 */
export function validateDataSchema(data, columnMapping = {}) {
  const errors = [];
  const warnings = [];

  if (!data || data.length === 0) {
    errors.push('No data found in the uploaded file');
    return { isValid: false, errors, warnings };
  }

  const availableColumns = Object.keys(data[0]);

  // Check required columns
  REQUIRED_COLUMNS.forEach((col) => {
    const mappedCol = columnMapping[col];
    if (!mappedCol) {
      errors.push(`Missing mapping for required column: ${col}`);
    } else if (!availableColumns.includes(mappedCol)) {
      errors.push(`Mapped column "${mappedCol}" for "${col}" not found in data`);
    }
  });

  // Check optional columns
  OPTIONAL_COLUMNS.forEach((col) => {
    const mappedCol = columnMapping[col];
    if (mappedCol && !availableColumns.includes(mappedCol)) {
      warnings.push(`Mapped column "${mappedCol}" for optional field "${col}" not found in data`);
    }
  });

  // Validate data quality
  if (errors.length === 0) {
    const valueCol = columnMapping.value;
    let invalidValueCount = 0;
    const blankCounts = REQUIRED_COLUMNS.reduce((counts, col) => ({
      ...counts,
      [col]: 0,
    }), {});

    data.forEach((row, index) => {
      REQUIRED_COLUMNS.forEach((col) => {
        const mappedCol = columnMapping[col];
        if (isBlankValue(row[mappedCol])) {
          blankCounts[col]++;
          if (blankCounts[col] <= 3) {
            errors.push(`Row ${index + 1}: Required field "${col}" is blank`);
          }
        }
      });

      const value = row[valueCol];
      if (!isBlankValue(value) && Number.isNaN(parseFloat(value))) {
        invalidValueCount++;
        if (invalidValueCount <= 3) {
          warnings.push(`Row ${index + 1}: Value "${value}" is not a valid number`);
        }
      }
    });

    if (invalidValueCount > 3) {
      warnings.push(`...and ${invalidValueCount - 3} more rows with invalid values`);
    }

    Object.entries(blankCounts).forEach(([col, count]) => {
      if (count > 3) {
        errors.push(`...and ${count - 3} more rows with blank "${col}" values`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Transform raw Excel data to MSDAT format
 * @param {Array} rawData - Raw data from Excel
 * @param {Object} columnMapping - Column mapping configuration
 * @returns {Array} - Transformed data in MSDAT format
 */
export function transformToMSDATFormat(rawData, columnMapping) {
  return rawData
    .map((row, index) => {
      const value = parseFloat(row[columnMapping.value]);

      // Skip rows with invalid values
      if (Number.isNaN(value)) {
        return null;
      }

      return {
        id: index + 1,
        indicator: row[columnMapping.indicator],
        location: row[columnMapping.location],
        period: String(row[columnMapping.period]),
        value,
        datasource: row[columnMapping.datasource],
        value_type: columnMapping.value_type ? row[columnMapping.value_type] : 'default',
        gender: columnMapping.gender ? row[columnMapping.gender] : null,
        age_group: columnMapping.age_group ? row[columnMapping.age_group] : null,
        category: columnMapping.category ? row[columnMapping.category] : null,
      };
    })
    .filter((row) => row !== null);
}

/**
 * Get unique values for a specific column
 * @param {Array} data - The data array
 * @param {string} column - The column name
 * @returns {Array} - Unique values
 */
export function getUniqueValues(data, column) {
  const values = new Set();
  data.forEach((row) => {
    if (row[column] !== null && row[column] !== undefined && row[column] !== '') {
      values.add(row[column]);
    }
  });
  return Array.from(values).sort();
}

/**
 * Generate summary statistics for the data
 * @param {Array} data - Transformed MSDAT data
 * @returns {Object} - Summary statistics
 */
export function generateDataSummary(data) {
  if (!data || data.length === 0) {
    return {
      totalRows: 0,
      indicators: [],
      locations: [],
      periods: [],
      datasources: [],
      valueRange: { min: 0, max: 0 },
    };
  }

  const indicators = getUniqueValues(data, 'indicator');
  const locations = getUniqueValues(data, 'location');
  const periods = getUniqueValues(data, 'period');
  const datasources = getUniqueValues(data, 'datasource');

  const values = data.map((row) => row.value).filter((v) => !Number.isNaN(v));

  return {
    totalRows: data.length,
    indicators,
    locations,
    periods,
    datasources,
    indicatorCount: indicators.length,
    locationCount: locations.length,
    periodCount: periods.length,
    datasourceCount: datasources.length,
    valueRange: {
      min: Math.min(...values),
      max: Math.max(...values),
      avg: values.reduce((a, b) => a + b, 0) / values.length,
    },
  };
}

/**
 * Filter data based on selected criteria
 * @param {Array} data - The data array
 * @param {Object} filters - Filter criteria
 * @returns {Array} - Filtered data
 */
export function filterData(data, filters = {}) {
  return data.filter((row) => {
    if (filters.indicators && filters.indicators.length > 0) {
      if (!filters.indicators.includes(row.indicator)) return false;
    }
    if (filters.locations && filters.locations.length > 0) {
      if (!filters.locations.includes(row.location)) return false;
    }
    if (filters.periods && filters.periods.length > 0) {
      if (!filters.periods.includes(row.period)) return false;
    }
    if (filters.datasources && filters.datasources.length > 0) {
      if (!filters.datasources.includes(row.datasource)) return false;
    }
    return true;
  });
}

/**
 * Group data by a specific field
 * @param {Array} data - The data array
 * @param {string} groupBy - Field to group by
 * @returns {Object} - Grouped data
 */
export function groupDataBy(data, groupBy) {
  const result = {};
  data.forEach((row) => {
    const key = row[groupBy];
    if (!result[key]) {
      result[key] = [];
    }
    result[key].push(row);
  });
  return result;
}

/**
 * Aggregate data for chart visualization
 * @param {Array} data - The data array
 * @param {Object} options - Aggregation options
 * @returns {Array} - Aggregated data for charts
 */
export function aggregateForChart(data, options = {}) {
  const { groupBy = 'location', aggregation = 'sum' } = options;
  const grouped = groupDataBy(data, groupBy);

  return Object.entries(grouped).map(([key, rows]) => {
    let value;
    const values = rows.map((r) => r.value);

    switch (aggregation) {
      case 'sum':
        value = values.reduce((a, b) => a + b, 0);
        break;
      case 'avg':
        value = values.reduce((a, b) => a + b, 0) / values.length;
        break;
      case 'min':
        value = Math.min(...values);
        break;
      case 'max':
        value = Math.max(...values);
        break;
      case 'count':
        value = values.length;
        break;
      default:
        value = values.reduce((a, b) => a + b, 0);
    }

    return {
      name: key,
      value: Math.round(value * 100) / 100,
      count: rows.length,
    };
  });
}

export default {
  parseExcelFile,
  extractColumns,
  autoDetectColumnMapping,
  validateDataSchema,
  transformToMSDATFormat,
  getUniqueValues,
  generateDataSummary,
  filterData,
  groupDataBy,
  aggregateForChart,
  REQUIRED_COLUMNS,
  OPTIONAL_COLUMNS,
};
